import { AgentInfoCard } from "components/AgentInfoCard";
import { fromUIToFilters } from "components/Filters/Filters.def";
import { MlsSearchCard } from "components/MlsSearchCard";
import { useLocation } from "react-router-dom";
import { isListingPath } from "utils/routes";
import { isProduction } from "scripts/application/setting";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { useUserContext } from "services/UserContext";
import { ISearchListingsArtifact } from "types/artifacts/search-listings";
import { IArtifactComponentProps } from "types/services";
import { findArtifactByHarid } from "utils/helpers";
import { eSearchType, SearchFilterOperation } from "operations/search-filter-operation";

export interface ISearchListingsOutputParams {
  search_id?: string;
  harid?: string;
}

export type ISearchListingsOutputProps = IArtifactComponentProps<
  ISearchListingsOutputParams,
  ISearchListingsArtifact
> & {
  /** Set by artifacts that reuse this renderer but are NOT search_listings
   * results. The "Load more" tile hard-codes a `search_listings` next-page
   * operation below, so on a reused renderer it would replace the results with
   * an unrelated listings search. */
  hideLoadMore?: boolean;
};

SearchListingsOutput.artifactKey = "search_listings";
export function SearchListingsOutput(
  props: ISearchListingsOutputProps
): JSX.Element {
  const { pathname } = useLocation();
  const { user } = useUserContext();
  const { cId } = useQueryFlags();
  const { search_id, harid: haridInput, artifacts, onNewMessage, message, hideLoadMore = false } = props;

  // if there is no valid artifacts return from the backend
  if (!Array.isArray(artifacts)) return <></>;

  if (search_id) {
    const artifact = artifacts.find((x) => x.id === search_id);
    const connectedAgent = user?.connected_agent;
    const isBrandedApp = !!(user.role === "consumer" && connectedAgent);
    const nameOrEmail = `${connectedAgent?.firstname} ${connectedAgent?.lastname}` || connectedAgent?.email || '';

    if (artifact) {
      return (
        <>
          {(isBrandedApp || cId) && (
            <div className="bg_color_cement_light border_radius--round12 mb-4 py-md-2 py-0">
              <div className="row no-gutters justify-content-center align-items-center ">
                <span className="font_size--large_extra px-3 py-2 col-md-auto col-12 font_size--large_sm font_weight--bold border-bottom border-md-bottom-0">Presented by</span>
                <div className="col-md col-12 px-3 py-2">
                  <AgentInfoCard
                    agentProfileUrl={connectedAgent?.bioUrl}
                    brokerProfileUrl={connectedAgent?.office_url || ''}
                    name={nameOrEmail}
                    photo={connectedAgent?.photo || ""}
                    officeName={connectedAgent?.office_name || ""}
                    mlsPlantinum={connectedAgent?.is_platinum}
                  />
                </div>
              </div>
            </div>
          )}
          <MlsSearchCard.Carousel
            artifact={artifact}
            // onFiltersNewMessage={(filters, operationName) => {
            //   sendMessage(filters, operationName, onNewMessage)
            // }}
            onFiltersNewMessage={(filters, type = eSearchType.Filter) => {
              const searchFilters = fromUIToFilters(filters);
              // Next page continues from the current page's offset; the backend's
              // apply_next_page advances it by the page size. Without this, start
              // defaults to 0 server-side and every "Load more" reloads page 2.
              if (type === eSearchType.NextPage) {
                searchFilters.start = artifact.response?.start ?? 0;
              }
              const operation = SearchFilterOperation.fromFilters("search_listings", searchFilters, type);
              onNewMessage(operation.getUserMessage(), operation);
            }}
            hideFiltersAndAiSummary={isListingPath(pathname)}
            hideLoadMore={hideLoadMore}
            message={message}
          />
        </>
      );
    }

    console.error(
      "No artifact found for 'SearchListingsOutput' with the given search_id:",
      search_id
    );

    return <></>;
  }

  if (haridInput) {
    const properties = artifacts.flatMap((x) => x.response.properties);
    const artifact = findArtifactByHarid(properties, haridInput);

    if (artifact) {
      return <MlsSearchCard details={artifact} />;
    }

    console.error(
      "No artifact found for 'SearchListingsOutput' with the given harid:",
      haridInput
    );

    return <></>;
  }

  console.error("No search_id or harid provided for 'SearchListingsOutput'.");

  return <></>;
}

SearchListingsOutput.asText = function (props: ISearchListingsOutputProps): string {
  // console.log("SearchListingsOutput.asText", props);
  return props?.artifacts && props.artifacts.length > 0 ? props.artifacts[0]?.response?.artifact_text || "" : isProduction ? "" : "SearchListingsOutput asText Not Implemented!";
}

// function sendMessage(
//   filters: IFilters,
//   operationName: QueryOperationName | undefined,
//   onNewMessage: (message: string, operation?: Operation) => void,
// ) {
//   const questionAndFilters = createFilterQuestionAndSelectedFilters(filters, 1);
//   if (questionAndFilters) {
//     operationName = operationName || "search_filter";
//     const { selectedFilters } = questionAndFilters;
//     const operation = QueryOperationFactory.create(operationName, "search_listings", selectedFilters, eQueryType.Filter);
//     onNewMessage(operation.getUserMessage(), operation);
//   }
// }